/*
 * The shapes the interface works in.
 *
 * They are close to what Iris Mail used, which was JMAP's vocabulary, and that is deliberate: the
 * components were written against these and they are a perfectly good way to describe mail. What
 * changed is underneath — there is no JMAP here, only the Go backend in ../server talking IMAP.
 *
 * The one thing worth knowing when reading this: a message is identified by the folder it is in
 * and its UID within that folder. IMAP has no id that survives a move, so `id` below is those two
 * things packed together, and it changes when a message moves. Anything holding an id across a
 * move has to refresh it.
 */

export interface Mailbox {
    /** The full IMAP path, which doubles as the id. */
    id: string;
    name: string;
    parentId: string | null;
    role: string | null;
    sortOrder: number;
    totalEmails: number;
    unreadEmails: number;
    isSubscribed: boolean;
    /** False for a folder that only exists to hold other folders. */
    selectable: boolean;
    depth: number;
    /** The server's stamp on this folder, sent back with anything destructive. */
    uidValidity?: number;
}

export interface EmailAddress {
    name: string | null;
    email: string;
}

export interface EmailBodyPart {
    /** The IMAP section number, "2.1". Only meaningful with the message it came from. */
    partId: string;
    size: number;
    name: string | null;
    type: string;
    disposition: string | null;
    cid: string | null;
}

export interface Email {
    /** folder + uid, packed. See packId. */
    id: string;
    folder: string;
    uid: number;
    uidValidity?: number;

    size: number;
    receivedAt: string;
    sentAt: string | null;
    subject: string | null;
    from: EmailAddress[] | null;
    to: EmailAddress[] | null;
    cc: EmailAddress[] | null;
    bcc: EmailAddress[] | null;
    replyTo: EmailAddress[] | null;
    messageId: string[] | null;
    inReplyTo: string[] | null;
    references: string[] | null;
    /*
        Set only when the folder is being listed as conversations. The row then stands for a group
        rather than a message. Absent from a flat listing rather than 1: a row that means one
        message should not have to say so.
    */
    threadCount?: number;
    threadUnread?: number;
    listUnsubscribe?: string | null;
    /** What the receiving server concluded about SPF, DKIM and DMARC. */
    authResults?: string | null;

    preview: string;
    hasAttachment: boolean;
    keywords: Record<string, boolean>;

    /** Present once the message has been opened, not in a listing. */
    attachments?: EmailBodyPart[];
    inline?: EmailBodyPart[];
    text?: string;
    html?: string;
    truncated?: boolean;
}

/** Flags, in the names the interface uses. The backend translates to IMAP's. */
export const KEYWORD = {
    seen: 'seen',
    flagged: 'flagged',
    answered: 'answered',
    draft: 'draft',
} as const;

export interface Identity {
    /** There is one identity: the mailbox that signed in. Their server has nothing else. */
    id: string;
    name: string;
    email: string;
    textSignature: string;
    htmlSignature: string;
}

export interface FolderStatus {
    folder: string;
    total: number;
    unseen: number;
    /** Moves when mail arrives, which is how the interface notices without fetching. */
    uidNext: number;
}

export interface SearchResults {
    matches: Email[];
    offset: number;
    total: number;
    /** Which folders were actually looked in. */
    searched: string[];
    /** True when the search stopped early rather than covering everything. */
    partial: boolean;
}

export interface SendResult {
    messageId: string;
    recipients: number;
    /** False on a message that was sent perfectly well but whose copy could not be filed. */
    savedToSent: boolean;
    sentFolder?: string;
    note?: string;
}

export class MailError extends Error {
    constructor(
        message: string,
        readonly status?: number,
        /** The mail server's own words, when it refused something. Worth showing for a bounce. */
        readonly reason?: string,
        /**
         * What kind of failure this is. 'unsupportedFilter' names a search term the backend
         * cannot express in IMAP, and the message is the term — the list layer drops it, says
         * so, and runs the rest of the search rather than failing the whole thing.
         */
        readonly type?: string,
    ) {
        super(message);
        this.name = 'MailError';
    }
}

/**
 * packId and unpackId turn a folder and a UID into one string and back.
 *
 * The uid goes first so the split is unambiguous: it is digits, so the first colon is always the
 * separator, whatever is in the folder name — and IMAP folder names can contain very nearly
 * anything, colons included.
 */
export function packId(folder: string, uid: number): string {
    return `${uid}:${folder}`;
}

export function unpackId(id: string): { folder: string; uid: number } {
    const at = id.indexOf(':');
    if (at < 0) return { folder: '', uid: 0 };
    return { uid: Number(id.slice(0, at)), folder: id.slice(at + 1) };
}

/*
    The address book.

    Present only where the installation has a DAV server — see `client.contacts()`, where a 404 is
    the supported answer rather than a failure.
*/
export interface ContactBook {
    path: string;
    name: string;
    /** A book belonging to the domain rather than the person. Editing one is a different act. */
    shared?: boolean;
}

export interface Contact {
    /** The card's path on the server; the thing that can be replaced and deleted. */
    id: string;
    name: string;
    email: string[];
    phone: string[];
    org?: string;
    note?: string;
    book: string;
    shared?: boolean;
}

/*
    The calendar.

    Present only where the installation has a DAV server, the same as the address book — and it is
    the same server: one deployment has both or neither.
*/
export interface Calendar {
    path: string;
    name: string;
    /** A calendar belonging to the domain rather than the person. Editing one is a different act. */
    shared?: boolean;
}

export interface CalendarEvent {
    /** The .ics path on the server; the thing that can be replaced and deleted. */
    id: string;
    uid: string;
    title: string;
    /**
     * An instant (RFC 3339) for a timed event, a plain date (`2026-09-12`) for an all-day one.
     *
     * Two kinds of thing, deliberately: an all-day event is a date and converting it to an instant
     * is how holidays end up on the wrong day. `end` is non-inclusive, as iCalendar has it.
     */
    start: string;
    end: string;
    allDay?: boolean;
    location?: string;
    note?: string;
    calendar: string;
    shared?: boolean;
    /**
     * One occurrence of a repeating event.
     *
     * The server expands repeats, so every occurrence arrives as its own row — but they all live in
     * one file and therefore share `id` and `uid`. Editing one changes the series, which is why the
     * screen says so before it lets anybody type.
     */
    recurring?: boolean;
}

/*
    Forwarding and the automatic reply.

    One Sieve script on the mail server carries both, because Sieve runs one script per mailbox and
    a second one would never run. That is why every call returns the whole state rather than the
    half that was changed.
*/

/** What happens to the copy that stays behind. `discard` is the server's word, not `nothing`. */
export type Keep = 'inbox' | 'folder' | 'discard';

export interface Forwarding {
    enabled: boolean;
    to: string[];
    keep: Keep;
    /** The folder a copy is filed into, when `keep` is `folder`. */
    folder: string;
}

export interface Vacation {
    enabled: boolean;
    /** `YYYY-MM-DD`, and either may be empty: no dates means "until I turn it off". */
    from: string;
    until: string;
    subject: string;
    body: string;
}

export interface RulesState {
    forwarding: Forwarding;
    vacation: Vacation;
    /** The name of the script the mail server is actually running, empty when it is running none. */
    active: string;
    /**
     * Whether the settings above are the ones in effect. A script can be stored, listed and still
     * do nothing, and telling somebody their mail is forwarded when it is not is the one mistake
     * this screen must never make.
     */
    running: boolean;
    /**
     * A script this webmail did not write. Then `script` carries its text, the settings are empty,
     * and every save is refused — mailboxes on this server forward through hand-written scripts,
     * and replacing one would stop mail somebody relies on.
     */
    foreign: boolean;
    script?: string;
    /** What the mail server's Sieve can do. What it cannot do is left out of the screen entirely. */
    canFileInto: boolean;
    canAutoReply: boolean;
    canScheduleReply: boolean;
    /**
     * How many addresses forwarding may name, or 0 when no limit is known. Enforced on the screen
     * because it cannot be enforced at save: a script over the limit is accepted, stored, made
     * active and read back unchanged, and then forwards to nobody at delivery.
     */
    maxTargets: number;
}

/**
 * One mailbox as the management service reports it.
 *
 * There is no password field and there will not be one: the service never sends a hash, and a
 * type that could carry one would be an invitation to show it.
 */
export interface AdminMailbox {
    adres: string;
    ad: string;
    aktif: boolean;
}

/**
 * What opening a domain's shared collections found or made. Each field is either "created" or
 * "already there" — running it again on a domain that already has them is a success, not a
 * failure, and the screen can say which without guessing.
 */
export interface AdminSharedState {
    path: string;
    root: string;
    contacts: string;
    calendar: string;
}
